import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import { buildSupportMailto } from "utils/supportMailto";

export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();

  const userInfo = useSelector((state) => state.common?.user_information);
  const clientId = useSelector((state) => state.common?.clientId);

  // Only offer Go Back when the previous entry is known to be inside the
  // portal, so navigate(-1) can never drop the user out of the product.
  //
  // Two ways that holds. `location.key` is "default" only on the first entry
  // of a router session, so anything else means an in-app navigation got us
  // here. That misses the common case though: a stale link clicked from
  // another portal page is a full page load, which resets the router and the
  // key. A same-origin referrer catches those, while still excluding a link
  // pasted from email or chat (referrer is empty or another site).
  //
  // window.history.length is unusable here: it counts the whole tab, so a link
  // opened in an existing tab looks identical to an in-app navigation.
  const cameFromPortal = document.referrer.startsWith(
    `${window.location.origin}/`
  );
  const canGoBack = location.key !== "default" || cameFromPortal;

  // Path only. The query string can carry contact identifiers, so it is never
  // rendered or mailed (buildSupportMailto strips it from the URL line too).
  const requestedPath = location.pathname;

  const mailtoHref = useMemo(
    () =>
      buildSupportMailto({
        userInfo,
        clientId,
        subjectDetail: `could not find ${requestedPath}`,
        // No `details`: with sanitizeUrl on, the URL line already carries the
        // requested path, so a Details block would just repeat it.
        sanitizeUrl: true,
      }),
    [userInfo, clientId, requestedPath]
  );

  return (
    // Sat at a fraction of the viewport height rather than centred, so the card
    // lands near eye level. Using vh over a centring offset keeps it from
    // clipping on a short window, where centring plus large padding would push
    // the top of the card out of the scroll container.
    <div className="tw:flex tw:min-h-full tw:flex-col tw:items-center tw:px-6 tw:pt-[12vh] tw:pb-12">
      <div className="tw:w-full tw:max-w-[500px] tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:px-9 tw:pt-10 tw:pb-8 tw:text-center tw:shadow-sm">
        <p className="tw:mb-3 tw:text-xs tw:font-medium tw:uppercase tw:tracking-widest tw:text-gray-500">
          Error 404
        </p>

        {/* The `!` utilities are load-bearing: main.scss styles bare `h1` as
            uppercase / 22px / #3f4040 from an unlayered sheet, which outranks
            Tailwind's layered utilities unless they are marked important. */}
        <h1 className="tw:!m-0 tw:!text-3xl tw:!font-medium tw:!normal-case tw:!text-gray-900">
          Page not found
        </h1>

        <p className="tw:mx-auto tw:mt-3 tw:max-w-[44ch] tw:text-base tw:text-gray-600">
          The link may be broken, or the page may have moved.
        </p>

        <div className="tw:mt-7 tw:flex tw:flex-wrap tw:justify-center tw:gap-3">
          <Button variant="secondary" onClick={() => navigate("/")}>
            Go Home
          </Button>

          {canGoBack && (
            <Button variant="outline" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          )}
        </div>

        <p className="tw:mt-7 tw:text-sm tw:text-gray-500">
          Still stuck?{" "}
          {/* `!` again: Bootstrap's reboot underlines every anchor from an
              unlayered sheet, which outranks a layered tw:no-underline. */}
          <a
            className="tw:font-medium tw:text-brand-secondary tw:!no-underline tw:hover:!underline"
            href={mailtoHref}
          >
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}
